import { StateParams, StateProvider, StateService } from "@uirouter/angularjs";
import { cloneDeep, uniq } from "lodash";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../records/server/serverUtility";
import { MachineGateway } from "./models/machine";

import {
  MachinesSoftwareViewController,
  HtmlTemplate as MachinesSoftwareViewHtmlTemplate,
} from "./views/machinesSoftware";
import {
  FormViewController,
  FormViewService,
  vsServiceName as FormViewServiceName,
  vsComponentName as FormViewComponentName,
} from "../../records/forms/formView";

import { IQService, IScope } from "angular";
import { vsServiceName as MachineDetailTabsServiceName } from "../shared/components/views/machines/detail/tabs";
import {
  IMachineEditFormModel,
  vsComponentName as MachineEditFormComponentName,
} from "./components/machineEditForm";
import { vsName as MachineProgressivesGridName } from "./components/progressives/grid";
import { vsName as progressivesDetailComponentComponentOptionsName } from "./components/progressives/detail";
import { ProgressiveDefinition } from "./models/progressiveDefinition";
import { IServiceResponse, ServiceResponse } from "../../records/server/query";
import { vsName as progressivesDetailMachinesComponentOptionsName } from "./components/progressives/detail/machines";
import {
  MachineConfigurationGateway,
  MachineConfigurationSoftware,
} from "./models/machineConfiguration";
import { vsName as progressivesDetailMachinesEditComponentOptionsName } from "./components/progressives/detail/machines/edit";
import { ProgressivesEditFormFields } from "./components/progressives/edit";
import { IMultipleSoftwareUpgradeFormModel } from "./views/machinesSoftware/SoftwareUpgrade";
import {
  ReportingServer,
  vsName as ReportingServerName,
} from "../../reporting/server";
import {
  CloneAndConversionServiceGateway,
  vsName as CloneAndConversionServiceName,
} from "./services/cloneAndConversionService";
import {
  AuthorizedUiElementConfig,
  AuthorizedUiElementService,
} from "../../ui/authorizedUiElement";
import { vsComponentName as mainMachinesGatewayComponentName } from "./components/views/machines/component";
import { IReportGroup } from "../../reporting/interfaces/IReportGroup";
import {
  RecordGridViewService,
  vsServiceName as RecordGridViewServiceName,
} from "../../records/views/recordGridView";
import { vsObjectName as machineConfigurationName } from "./config/machineConfiguration";

export function routeConfig($stateProvider: StateProvider) {
  $stateProvider
    .state("main.machines", {
      url: "machines",
      template: "<ui-view></ui-view>",
      abstract: true,
      resolve: {
        reportGroup: [
          ReportingServerName,
          function (reportingServer: ReportingServer) {
            return reportingServer.getReportGroup("machine").then((rg) => {
              return rg;
            });
          },
        ],
      },
    })
    .state("main.machines.software", {
      url: "machineSoftware",
      controller: MachinesSoftwareViewController,
      controllerAs: "stateController",
      template: MachinesSoftwareViewHtmlTemplate,
    })
    .state("main.machines.grid", {
      url: "",
      component: mainMachinesGatewayComponentName,
      resolve: {
        gridConfiguration: [
          RecordGridViewServiceName,
          "$state",
          (
            recordGridViewService: RecordGridViewService,
            $state: StateService
          ) => {
            const gridConfiguration =
              recordGridViewService.getGridConfiguration(
                machineConfigurationName
              );

            gridConfiguration!.onAddNew = () =>
              $state.go("^.add").then(() => {});

            return gridConfiguration;
          },
        ],
      },
    })
    .state("main.machines.add", {
      url: "/add",
      component: MachineEditFormComponentName,
      params: {
        modelMachineId: undefined,
        modelMachineConfigurationId: undefined,
      },
      resolve: {
        model: [
          "$stateParams",
          CloneAndConversionServiceName,
          (
            $stateParams: StateParams,
            cloneAndConversionService: CloneAndConversionServiceGateway
          ) => {
            const modelMachineId = $stateParams.modelMachineId;
            const modelMachineConfigurationId =
              $stateParams.modelMachineConfigurationId;

            if (!modelMachineId) {
              return {
                Configuration: { Status: "Floor" },
                Machine: {},
              };
            }

            const cloneConfiguration =
              cloneAndConversionService.getCloneConfiguration(
                modelMachineId,
                modelMachineConfigurationId
              );

            return cloneConfiguration;
          },
        ],
        progressiveDefinition: [
          "model",
          CloneAndConversionServiceName,
          (
            model: IMachineEditFormModel,
            cloneAndConversionService: CloneAndConversionServiceGateway
          ) =>
            cloneAndConversionService.getProgressiveDefinition(
              model.Configuration.ProgressiveDefinitionId
            ),
        ],
        machineFieldsEditable: [() => true],
        onSubmit: [
          FormViewServiceName,
          MachineDetailTabsServiceName,
          "$state",
          (
              formViewService: FormViewService,
              machineDetailTabsService: AuthorizedUiElementService<AuthorizedUiElementConfig>,
              $state: StateService
            ) =>
            (
              formViewController: FormViewController,
              model: IMachineEditFormModel
            ) =>
              formViewService
                .submitForm<ServiceResponse<MachineGateway>>({
                  url: "/api/machines/Machine",
                  method: "post",
                  formObject: model,
                  formViewController,
                })
                .then((result) => {
                  machineDetailTabsService.navigateToChildByIndex(0, {
                    machineId: result.Result.Id,
                  });
                }),
        ],
        onCancel: [
          "$state",
          ($state: StateService) => () => $state.go("^.grid"),
        ],
        formTitle: [() => "Add Machine"],
      },
    })
    .state("main.machines.detail", {
      url: "/{machineId:int}",
      template: require("!html-loader!./machineDetail.html").default,
      controller: [
        "$scope",
        "machine",
        "reportGroup",
        "progressiveDefinition",
        (
          $scope: IScope,
          machine: MachineGateway,
          reportGroup: IReportGroup,
          progressiveDefinition: ProgressiveDefinition
        ) => {
          const currentConfiguration = machine.Configs?.find(
            (c) => c.CurrentConfiguration
          );
          const s = <any>$scope;
          s.machine = machine;
          s.machineConfiguration = currentConfiguration;
          s.reportGroup = reportGroup;
          s.progressiveDefinition = progressiveDefinition;
          s.editSref = ".edit";
        },
      ],
      resolve: {
        machine: [
          ServerUtilityName,
          "$stateParams",
          function (serverUtility: ServerUtility, $stateParams: StateParams) {
            const machineId = $stateParams["machineId"];
            return serverUtility
              .getById<IServiceResponse<MachineGateway>>({
                url: "/api/machines/Machine",
                id: machineId,
              })
              .then((r) => r.Result);
          },
        ],
        progressiveDefinition: [
          ServerUtilityName,
          "machine",
          "$q",
          (
            serverUtility: ServerUtility,
            machine: MachineGateway,
            $q: IQService
          ) => {
            const progressiveDefinitionIds =
              uniq(
                machine.Configs?.map((c) => c.ProgressiveDefinitionId).filter(
                  (i) => i
                )
              ) ?? [];
            return progressiveDefinitionIds.length
              ? serverUtility
                  .sendApiRequest({
                    method: "get",
                    url: "/api/machines/progressives/ProgressiveDefinition/ByIds",
                    params: { Ids: progressiveDefinitionIds },
                  })
                  .then(
                    (result) =>
                      (<IServiceResponse<ProgressiveDefinition[]>>result).Result
                  )
              : $q.when([]);
          },
        ],
      },
    })
    .state("main.machines.detail.edit", {
      url: "/edit/{machineConfigurationId:int}",
      component: MachineEditFormComponentName,
      resolve: {
        model: [
          "machine",
          "$stateParams",
          (machine: MachineGateway, $stateParams: StateParams) => {
            const configurationId = $stateParams.machineConfigurationId;
            const configuration = machine.Configs?.find(
              (c) => c.Id === configurationId
            );

            const modelMachine = cloneDeep(machine);
            delete modelMachine.Configs;

            return {
              Machine: modelMachine,
              Configuration: cloneDeep(configuration),
            };
          },
        ],
        progressiveDefinition: [
          "model",
          CloneAndConversionServiceName,
          (
            model: IMachineEditFormModel,
            cloneAndConversionService: CloneAndConversionServiceGateway
          ) =>
            cloneAndConversionService.getProgressiveDefinition(
              model.Configuration.ProgressiveDefinitionId
            ),
        ],
        machineFieldsEditable: [() => true],
        onSubmit: [
          FormViewServiceName,
          MachineDetailTabsServiceName,
          "$q",
          (
              formViewService: FormViewService,
              machineDetailTabsService: AuthorizedUiElementService<AuthorizedUiElementConfig>,
              $q: IQService
            ) =>
            (
              formViewController: FormViewController,
              model: IMachineEditFormModel
            ) => {
              const { form } = formViewController;

              const machineId = model.Machine.Id;
              const configurationId = model.Configuration.Id;

              const updatePromises = [
                formViewService.submitForm({
                  formObject: model.Configuration,
                  formViewController: formViewController,
                  method: "PUT",
                  url: `/api/machines/MachineConfiguration/${configurationId}`,
                  validationPrefix: "Configuration",
                }),
              ];

              const anyDirtyMachineFields =
                form
                  .$getControls()
                  .find((f) => f.$name?.startsWith("Machine.") && f.$dirty) !==
                undefined;
              if (anyDirtyMachineFields) {
                updatePromises.push(
                  formViewService.submitForm({
                    formObject: model.Machine,
                    formViewController: formViewController,
                    method: "PUT",
                    url: `/api/machines/Machine/${machineId}`,
                    validationPrefix: "Machine",
                  })
                );
              }

              return $q
                .all(updatePromises)
                .then(() =>
                  machineDetailTabsService.navigateToChildByIndex(
                    0,
                    { machineId },
                    true
                  )
                );
            },
        ],
        onCancel: [
          MachineDetailTabsServiceName,
          "$stateParams",
          (
              machineDetailTabsService: AuthorizedUiElementService<AuthorizedUiElementConfig>,
              $stateParams: StateParams
            ) =>
            () => {
              return machineDetailTabsService.navigateToChildByIndex(0, {
                machineId: $stateParams.machineId,
              });
            },
        ],
        formTitle: [() => "Edit Machine"],
      },
    })
    .state("main.machines.software.SoftwareUpgrade", {
      url: "/SoftwareUpgrade",
      component: "machinesSoftwareUpgradeView",
      params: {
        softwares: { value: [] },
      },
      resolve: {
        model: [
          "$stateParams",
          function ($stateParams: {
            softwares: MachineConfigurationSoftware[];
          }): IMultipleSoftwareUpgradeFormModel {
            return {
              MachineConfigurationSoftwareIds: $stateParams.softwares.map(
                (s) => s.Id
              ),
              MachineConfigurationSoftware: {
                Id: 0,
                Software: "",
                Version: "",
              },
            };
          },
        ],
      },
      bindings: {
        model: "model",
      },
    })
    .state("main.machines.progressives", {
      url: "/progressives",
      component: MachineProgressivesGridName,
      bindings: {},
    })
    .state("main.machines.progressives.detail", {
      url: "/{progressiveDefinitionId:int}",
      params: { progressiveDefinitionId: null },
      component: progressivesDetailComponentComponentOptionsName,
      resolve: {
        progressiveDefinition: [
          "$stateParams",
          ServerUtilityName,
          ($stateParams: StateParams, serverUtility: ServerUtility) =>
            serverUtility
              .getById<IServiceResponse<ProgressiveDefinition>>({
                url: "/api/machines/progressives/ProgressiveDefinition",
                id: $stateParams.progressiveDefinitionId,
              })
              .then((result) => result.Result),
        ],
      },
    })
    .state("main.machines.progressives.detail.edit", {
      url: "/edit",
      component: FormViewComponentName,
      resolve: {
        title: [() => "Edit Progressive Definition"],
        fields: ProgressivesEditFormFields,
        model: [
          ServerUtilityName,
          "$stateParams",
          (serverUtility: ServerUtility, $stateParams: StateParams) =>
            serverUtility
              .getById({
                url: "/api/machines/progressives/ProgressiveDefinition",
                id: $stateParams.progressiveDefinitionId,
              })
              .then(
                (response: unknown) =>
                  (<IServiceResponse<ProgressiveDefinition>>response).Result
              ),
        ],
        onSubmit: [
          FormViewServiceName,
          "$state",
          "$stateParams",
          (
            formViewService: FormViewService,
            $state: StateService,
            $stateParams: StateParams
          ) => [
            "model",
            "formViewController",
            (
              model: ProgressiveDefinition,
              formViewController: FormViewController
            ) =>
              formViewService
                .submitForm({
                  url: `/api/machines/progressives/ProgressiveDefinition/${$stateParams.progressiveDefinitionId}`,
                  formObject: model,
                  formViewController: formViewController,
                  method: "PUT",
                })
                .then(() => {
                  $state.go("^.machines", undefined, { reload: true });
                }),
          ],
        ],
        onCancel: [
          "$state",
          ($state: StateService) => [() => $state.go("^.machines")],
        ],
      },
    })
    .state("main.machines.progressives.add", {
      url: "/add",
      component: FormViewComponentName,
      params: {
        progressiveDefinition: undefined,
      },
      resolve: {
        title: [() => "Add Progressive"],
        fields: ProgressivesEditFormFields,
        model: [
          "$stateParams",
          ($stateParams: StateParams) => {
            return $stateParams.progressiveDefinition ?? {};
          },
        ],
        onSubmit: [
          FormViewServiceName,
          "$state",
          (formViewService: FormViewService, $state: StateService) => {
            return [
              "model",
              "formViewController",
              (
                model: ProgressiveDefinition,
                formViewController: FormViewController
              ) =>
                formViewService
                  .submitForm({
                    url: "/api/machines/progressives/ProgressiveDefinition",
                    formObject: model,
                    formViewController: formViewController,
                    method: "POST",
                  })
                  .then((result) => {
                    const progressiveDefinition = (<
                      IServiceResponse<ProgressiveDefinition>
                    >result).Result;
                    $state.go("^.detail.machines", {
                      progressiveDefinitionId: progressiveDefinition.Id,
                    });
                  }),
            ];
          },
        ],
        onCancel: ["$state", ($state: StateService) => () => $state.go("^")],
      },
    })
    .state("main.machines.progressives.detail.machines", {
      url: "/machines",
      component: progressivesDetailMachinesComponentOptionsName,
    })
    .state("main.machines.progressives.detail.machines.edit", {
      url: "/edit",
      component: progressivesDetailMachinesEditComponentOptionsName,
      resolve: {
        progressiveMachineConfigurations: [
          ServerUtilityName,
          "$stateParams",
          (serverUtility: ServerUtility, $stateParams: StateParams) => {
            const progressiveDefinitionId =
              $stateParams.progressiveDefinitionId;
            return serverUtility.fetchAll<MachineConfigurationGateway>({
              url: "/api/machines/MachineConfiguration",
              query: { ProgressiveDefinitionId: progressiveDefinitionId },
            });
          },
        ],
      },
    });
}
routeConfig.$inject = ["$stateProvider"];
